/**
 * Les deux nombres de sources (DEV-621, écran M5).
 *
 * INTENTION. Le client lit deux nombres nommés et jamais mélangés, tous deux
 * servis par l'API dans le bloc `plafond` : les sources « sur votre compte »
 * (les deux premières feuilles de l'arbre, le même nombre que sur la page
 * d'abonnement) et les sources « facturées sur la période ». Sans bloc servi,
 * rien n'est écrit : une absence n'est jamais un 0.
 *
 * Règles de rédaction : « facturées », jamais « possédez » ni « compteurs »
 * pour ces nombres, aucun nom de marque, aucun détail technique.
 */
import type { CompanyPlafond } from "@core/types/auth"

const estNombre = (x: unknown): x is number =>
    typeof x === "number" && Number.isFinite(x) && x >= 0

/**
 * Sources sur le compte servies (deux premières feuilles), `null` si la
 * composition manque ou contredit le total facturé servi (somme des quatre
 * feuilles facturées = `facturees`, même garde que la page d'abonnement).
 */
export function surVotreCompteServi(
    plafond:
        | Pick<CompanyPlafond, "facturees" | "composition">
        | null
        | undefined
): number | null {
    const c = plafond?.composition
    if (!plafond || !c) return null
    const surLeCompte =
        c.sur_le_compte_ajoutees_dans_la_periode +
        c.sur_le_compte_ajoutees_avant
    const facturees =
        surLeCompte +
        c.supprimees_ajoutees_dans_la_periode +
        c.supprimees_apres_mise_a_jour
    if (!estNombre(surLeCompte) || facturees !== plafond.facturees) return null
    return surLeCompte
}

/**
 * « 92 sur votre compte, 116 facturées sur la période », à partir du seul
 * bloc servi. Sans composition lisible : « 116 facturées sur la période ».
 * @param plafond - bloc `plafond` servi (store), `null` s'il ne l'est pas
 * @returns le texte, ou `null` sans nombre facturé servi (rien n'est affiché)
 */
export function texteDeuxNombres(
    plafond:
        | Pick<CompanyPlafond, "facturees" | "composition">
        | null
        | undefined
): string | null {
    const m = plafond?.facturees
    if (!estNombre(m)) return null
    const facturees = `${m} ${m > 1 ? "facturées" : "facturée"} sur la période`
    const x = surVotreCompteServi(plafond)
    return x === null ? facturees : `${x} sur votre compte, ${facturees}`
}
